import { useMemo, useState } from 'react';
import { api } from '../../api/client';
import type { Deliverable } from '@pixerito/api-client';
import { DeliverableCard } from '../../components/DeliverableCard';
import { Empty, ErrorNotice, Loading } from '../../components/ui';
import { useAsync } from '../../lib/hooks';
import { MODULE_LABEL } from '../../lib/format';

type Filter = 'open' | 'delivered' | 'all';

/** Central de Entregáveis: identidade visual e vídeo, com status, arquivo e versões. */
export function DeliverablesCenter({
  items,
  actions,
  emptyAction,
}: {
  items: Deliverable[];
  actions?: (d: Deliverable) => React.ReactNode;
  emptyAction?: React.ReactNode;
}) {
  const [filter, setFilter] = useState<Filter>('open');
  const [module, setModule] = useState<'all' | 'brand' | 'video'>('all');
  const modules = useMemo(() => [...new Set(items.map((d) => d.module))], [items]);
  const counts = {
    open: items.filter((d) => d.status !== 'delivered').length,
    delivered: items.filter((d) => d.status === 'delivered').length,
    all: items.length,
  };
  const effective: Filter = filter === 'open' && counts.open === 0 && counts.all > 0 ? 'all' : filter;
  const shown = items.filter(
    (d) =>
      (effective === 'all' || (effective === 'open' ? d.status !== 'delivered' : d.status === 'delivered')) &&
      (module === 'all' || d.module === module),
  );

  if (items.length === 0) {
    return (
      <Empty title="Nenhum entregável por enquanto" action={emptyAction}>
        Cada peça de identidade visual ou vídeo aparece aqui com o andamento e o arquivo final.
      </Empty>
    );
  }

  return (
    <div className="stack-lg">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div className="tabs" role="tablist" aria-label="Filtrar por andamento">
          {(['open', 'delivered', 'all'] as Filter[]).map((f) => (
            <button key={f} role="tab" aria-selected={effective === f} onClick={() => setFilter(f)}>
              {f === 'open' ? 'Em andamento' : f === 'delivered' ? 'Entregues' : 'Todos'}{' '}
              <span className="num" style={{ opacity: 0.7 }}>
                {counts[f]}
              </span>
            </button>
          ))}
        </div>
        {modules.length > 1 && (
          <div className="tabs" role="tablist" aria-label="Filtrar por serviço">
            {(['all', ...modules] as const).map((m) => (
              <button key={m} role="tab" aria-selected={module === m} onClick={() => setModule(m)}>
                {m === 'all' ? 'Todos os serviços' : MODULE_LABEL[m]}
              </button>
            ))}
          </div>
        )}
      </div>

      {shown.length === 0 ? (
        <p className="muted t-small">Nada por aqui com esse filtro.</p>
      ) : (
        <div className="deliv-grid">
          {shown.map((d) => (
            <DeliverableCard key={d.id} deliverable={d} showModule={modules.length > 1} actions={actions?.(d)} />
          ))}
        </div>
      )}
    </div>
  );
}

export function ClientDeliverablesPage() {
  const { data, error, loading, reload } = useAsync(() => api.myDeliverables(), []);
  return (
    <>
      <div className="page-head">
        <div className="stack" style={{ gap: 6 }}>
          <p className="eyebrow">&lt;_Central_de_Entregáveis/&gt;</p>
          <h1 className="t-display">Suas entregas</h1>
          <p className="t-sub">Acompanhe cada peça da produção até o arquivo final.</p>
        </div>
      </div>
      {loading && !data ? (
        <Loading />
      ) : error ? (
        <ErrorNotice message={error} onRetry={reload} />
      ) : (
        data && <DeliverablesCenter items={data} />
      )}
    </>
  );
}
